import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import api from '@/lib/api';

// Source language is Spanish. These are the selectable target languages.
export const LANGS = [
  { code: 'es', native: 'Español', label: 'ES' },
  { code: 'en', native: 'English', label: 'EN' },
  { code: 'de', native: 'Deutsch', label: 'DE' },
  { code: 'it', native: 'Italiano', label: 'IT' },
  { code: 'pt', native: 'Português', label: 'PT' },
  { code: 'fr', native: 'Français', label: 'FR' },
  { code: 'ja', native: '日本語', label: 'JA' },
  { code: 'zh', native: '中文', label: 'ZH' },
  { code: 'ar', native: 'العربية', label: 'AR' },
];
const RTL = new Set(['ar']);
const STORAGE_KEY = 'app_lang';

// Localized "translated automatically" note (kept out of runtime translation so it is instant + correct).
export const AUTO_NOTE = {
  es: 'Traducción automática por IA · puede variar',
  en: 'Automatically translated by AI · may vary',
  de: 'Automatisch von KI übersetzt · kann abweichen',
  it: 'Tradotto automaticamente dall’IA · può variare',
  pt: 'Traduzido automaticamente por IA · pode variar',
  fr: 'Traduit automatiquement par IA · peut varier',
  ja: 'AIによる自動翻訳 · 内容が異なる場合があります',
  zh: 'AI 自动翻译 · 可能存在差异',
  ar: 'ترجمة تلقائية بالذكاء الاصطناعي · قد تختلف',
};

/* ------------------------- runtime translation engine ------------------------- */
const memCache = {}; // { [lang]: Map(source->translation) }
const getCache = (lang) => (memCache[lang] || (memCache[lang] = new Map()));
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'CODE', 'PRE', 'SVG']);
const ATTRS = ['placeholder', 'title', 'aria-label', 'alt'];
const HAS_LETTER = /[A-Za-zÀ-ÿ\u00C0-\u024F]/;

const translatable = (s) => {
  if (!s) return false;
  const t = s.trim();
  if (t.length < 2 || t.length > 800) return false;
  if (!HAS_LETTER.test(t)) return false;
  return true;
};

function collectNodes() {
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const p = node.parentElement;
      if (!p) return NodeFilter.FILTER_REJECT;
      if (SKIP_TAGS.has(p.tagName)) return NodeFilter.FILTER_REJECT;
      if (p.closest('[data-noi18n]')) return NodeFilter.FILTER_REJECT;
      if (p.isContentEditable) return NodeFilter.FILTER_REJECT;
      const src = node.__i18n_src != null ? node.__i18n_src : node.nodeValue;
      return translatable(src) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
    },
  });
  const nodes = [];
  let n;
  while ((n = walker.nextNode())) nodes.push(n);
  return nodes;
}

function collectAttrs() {
  const items = [];
  document.body.querySelectorAll('[' + ATTRS.join('],[') + ']').forEach((el) => {
    if (el.closest('[data-noi18n]')) return;
    ATTRS.forEach((a) => {
      const stored = el.__i18n_attr && el.__i18n_attr[a];
      const orig = stored != null ? stored : el.getAttribute(a);
      if (orig && translatable(orig)) items.push({ el, attr: a, src: orig });
    });
  });
  return items;
}

async function fetchTranslations(lang, sources) {
  const cache = getCache(lang);
  const missing = sources.filter((s) => !cache.has(s));
  for (let i = 0; i < missing.length; i += 300) {
    const chunk = missing.slice(i, i + 300);
    try {
      const { data } = await api.post('/i18n/translate', { lang, texts: chunk });
      const tr = (data && data.translations) || {};
      chunk.forEach((s) => cache.set(s, tr[s] != null ? tr[s] : s));
    } catch (e) {
      chunk.forEach((s) => cache.set(s, s));
    }
  }
}

function restoreSpanish() {
  collectNodes().forEach((n) => {
    if (n.__i18n_src != null) { if (n.nodeValue !== n.__i18n_src) n.nodeValue = n.__i18n_src; delete n.__i18n_src; }
  });
  document.body.querySelectorAll('[' + ATTRS.join('],[') + ']').forEach((el) => {
    if (el.__i18n_attr) { ATTRS.forEach((a) => { if (el.__i18n_attr[a] != null) el.setAttribute(a, el.__i18n_attr[a]); }); delete el.__i18n_attr; }
  });
}

async function applyLang(lang, setBusy) {
  if (!document.body) return;
  if (lang === 'es') { restoreSpanish(); return; }
  const nodes = collectNodes();
  const attrs = collectAttrs();
  const sources = new Set();
  nodes.forEach((n) => sources.add((n.__i18n_src != null ? n.__i18n_src : n.nodeValue).trim()));
  attrs.forEach((a) => sources.add(a.src.trim()));
  if (!sources.size) return;
  const cache = getCache(lang);
  const need = [...sources].some((s) => !cache.has(s));
  if (need && setBusy) setBusy(true);
  await fetchTranslations(lang, [...sources]);
  nodes.forEach((n) => {
    const src = n.__i18n_src != null ? n.__i18n_src : n.nodeValue;
    const trimmed = src.trim();
    const dst = cache.get(trimmed);
    if (dst && dst !== trimmed) {
      const lead = src.match(/^\s*/)[0], trail = src.match(/\s*$/)[0];
      const val = lead + dst + trail;
      if (n.nodeValue !== val) { n.__i18n_src = src; n.nodeValue = val; }
    }
  });
  attrs.forEach(({ el, attr, src }) => {
    const dst = cache.get(src.trim());
    if (dst && dst !== src.trim() && el.getAttribute(attr) !== dst) {
      el.__i18n_attr = el.__i18n_attr || {};
      if (el.__i18n_attr[attr] == null) el.__i18n_attr[attr] = el.getAttribute(attr);
      el.setAttribute(attr, dst);
    }
  });
  if (setBusy) setBusy(false);
}

/* ------------------------------- React context ------------------------------- */
const I18nContext = createContext({ lang: 'es', setLang: () => {}, dir: 'ltr', translating: false, LANGS });
export const useI18n = () => useContext(I18nContext);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => (typeof window !== 'undefined' && localStorage.getItem(STORAGE_KEY)) || 'es');
  const [translating, setTranslating] = useState(false);
  const running = useRef(false);
  const rerun = useRef(false);
  const langRef = useRef(lang);
  langRef.current = lang;

  const schedule = useCallback((l) => {
    if (running.current) { rerun.current = true; return; }
    running.current = true;
    Promise.resolve(applyLang(l, setTranslating)).finally(() => {
      running.current = false;
      if (rerun.current) { rerun.current = false; schedule(langRef.current); }
    });
  }, []);

  // Reflect lang + dir on <html>; persist.
  useEffect(() => {
    const dir = RTL.has(lang) ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('lang', lang);
    document.documentElement.setAttribute('dir', dir);
    localStorage.setItem(STORAGE_KEY, lang);
    schedule(lang);
  }, [lang, schedule]);

  // Observe DOM changes and (re)translate incrementally.
  useEffect(() => {
    let timer;
    const obs = new MutationObserver(() => {
      if (langRef.current === 'es') return;
      clearTimeout(timer);
      timer = setTimeout(() => schedule(langRef.current), 350);
    });
    obs.observe(document.body, { childList: true, subtree: true, characterData: true });
    return () => { clearTimeout(timer); obs.disconnect(); };
  }, [schedule]);

  const setLang = useCallback((l) => setLangState(l), []);
  const value = { lang, setLang, dir: RTL.has(lang) ? 'rtl' : 'ltr', translating, LANGS };
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
